import React from "react";
import { Link } from "react-router-dom";
import SiteShell from "../../components/layout/SiteShell";
import ContentPageLayout from "../../components/content/ContentPageLayout";
import SEO from "../../components/seo/SEO";
import ConversionCtas from "../../components/content/ConversionCtas";
import WellnessDisclaimer from "../../components/content/WellnessDisclaimer";
import DarkCard from "../../components/layout/DarkCard";
import { getLifestyleImage } from "../../lib/lifestyleImages";
import { HOME_CTA_DARK_OUTLINE, HOME_CTA_DARK_PRIMARY } from "../../lib/homeCta";

function ExtLink({ href, children }) {
  return (
    <a href={href} target="_blank" rel="noopener noreferrer" className="underline hover:text-goldhover">
      {children}
    </a>
  );
}

const READINGS = [
  {
    title: "Seaweed nutritional value and bioactive properties (includes Chondrus crispus)",
    href: "https://pmc.ncbi.nlm.nih.gov/articles/PMC11595611/",
    note: "Open-access paper comparing mineral and nutrient profiles of three edible seaweeds, including Irish moss.",
  },
  {
    title: "Chemical constituents and biological properties of the genus Chondrus — review",
    href: "https://pmc.ncbi.nlm.nih.gov/articles/PMC10817618/",
    note: "Peer-reviewed review of Chondrus species; notes many findings are from lab or animal models, with limited clinical human data.",
  },
  {
    title: "FAO: A guide to the seaweed industry (Technical Paper 441)",
    href: "https://www.fao.org/4/y4765e/y4765e00.htm",
    note: "Industry and food overview; Irish moss (Chondrus crispus) as a traditional food and historical carrageenan source.",
  },
  {
    title: "NIH ODS — Iodine: Fact Sheet for Consumers",
    href: "https://ods.od.nih.gov/factsheets/Iodine-Consumer/",
    note: "Clear background on iodine needs, food sources (including seaweed), and upper intake limits.",
  },
  {
    title: "Risk assessment of iodine intake from red seaweeds (P. palmata & C. crispus)",
    href: "https://pubmed.ncbi.nlm.nih.gov/32803579/",
    note: "PubMed abstract: measured iodine in retail red seaweeds and discussed portion-size context.",
  },
  {
    title: "EFSA re-evaluation of carrageenan (E 407) and processed Eucheuma seaweed (E 407a)",
    href: "https://efsa.onlinelibrary.wiley.com/doi/10.2903/j.efsa.2018.5238",
    note: "Regulatory safety review of food-grade carrageenan; temporary ADI while further data are collected.",
  },
  {
    title: "Food additive carrageenan: critical review of in vivo safety studies",
    href: "https://pubmed.ncbi.nlm.nih.gov/24467586/",
    note: "Peer-reviewed review distinguishing food-grade carrageenan from degraded forms used in some lab models.",
  },
  {
    title: "Emergence of seaweed foods in the UK — labelling, iodine, toxicity, nutrition",
    href: "https://pmc.ncbi.nlm.nih.gov/articles/PMC5302319/",
    note: "UK-focused look at seaweed products, iodine labelling gaps, and consumer exposure questions.",
  },
  {
    title: "National Geographic — Sea moss trend: benefits, hype, and risks",
    href: "https://www.nationalgeographic.com/health/article/sea-moss-health-benefits-effects",
    note: "Journalist summary with clinicians and dietitians; useful for balanced public framing.",
  },
];

const VIDEOS = [
  {
    id: "nK1ps4o-zrY",
    title: "National Seaweed Hub webinar — seaweed nutrition (Jinzeng Yang)",
    blurb: "University / Sea Grant–style webinar on seaweed carbohydrates, iodine, and how people actually eat seaweed with other foods.",
  },
  {
    id: "93nk2xIRcbk",
    title: "BBC Earth — Are underwater farms the future of food?",
    blurb: "Documentary excerpt on seaweed farming as food and ocean restoration context — not a product pitch.",
  },
  {
    id: "p_jCa8jE4Rc",
    title: "How real Irish moss (Chondrus crispus) gel is made",
    blurb: "Practical species note: Chondrus crispus vs other “sea moss” sold online, plus simple gel preparation methods.",
  },
];

export default function SeaMossResearchPage() {
  const path = "/learn/sea-moss-research";
  const ocean = getLifestyleImage("heroLifestyle");
  const kitchen = getLifestyleImage("routineKitchen");

  return (
    <SiteShell theme="dark">
      <SEO
        title="Sea Moss Research: Evidence Snapshot | Moss Matrix"
        description="Claim-safe educational overview of sea moss (Irish moss / Chondrus crispus) research: nutrient profiles, traditional food use, iodine, and carrageenan debates — with real citations. Not medical advice."
        path={path}
      />
      <ContentPageLayout
        eyebrow="Learn · Research"
        title="Sea moss research, without the hype."
        subtitle="A short, evidence-backed reading guide to Irish moss / Chondrus crispus and related sea moss gels — what studies and food agencies actually discuss, and what remains early or mixed."
      >
        <DarkCard className="mb-10 max-w-3xl border border-gold/25">
          <p className="text-sm text-sage leading-relaxed m-0">
            This page is food and nutrition education only. Moss Matrix Sea Moss Gel is sold as a food product, not a
            medicine or supplement. We do not claim that sea moss cures, treats, or prevents disease. For personal
            health decisions, use our{" "}
            <Link to="/learn/sea-moss-benefits" className="underline hover:text-goldhover">
              benefits education guide
            </Link>
            , browse the{" "}
            <Link to="/learn/sea-moss-nutrition" className="underline hover:text-goldhover">
              minerals & amino acids resource hub
            </Link>
            , and the disclaimer below.
          </p>
        </DarkCard>

        <div className="rounded-[1.5rem] overflow-hidden border border-white/10 mb-12 max-w-4xl">
          <img
            src={ocean.src}
            alt={ocean.alt}
            width={ocean.width}
            height={ocean.height}
            className="w-full aspect-[21/9] object-cover"
          />
          <p className="px-4 py-2 text-[10px] text-sage/70 bg-panel/80">
            Coastal atmosphere — mood imagery, not a lab claim.
          </p>
        </div>

        <div className="space-y-5 max-w-3xl mb-12">
          <DarkCard as="section" aria-labelledby="what-it-is">
            <h2 id="what-it-is" className="font-sans font-semibold text-xl text-ivory m-0">
              What “sea moss” usually means in research
            </h2>
            <p className="mt-3 text-sm text-sage leading-relaxed m-0">
              In scientific and industry writing, “Irish moss” most often means{" "}
              <em className="text-ivory/90">Chondrus crispus</em>, a red seaweed from cool North Atlantic coasts. The{" "}
              <ExtLink href="https://www.fao.org/4/y4765e/y4765e00.htm">FAO seaweed industry guide</ExtLink> also
              describes it as a traditional human food and the historical source of carrageenan. Retail “sea moss” gels
              may use <em className="text-ivory/90">C. crispus</em> or other red algae (for example{" "}
              <em className="text-ivory/90">Gracilaria</em>) — species and processing matter for composition.
            </p>
          </DarkCard>

          <DarkCard as="section" aria-labelledby="nutrients">
            <h2 id="nutrients" className="font-sans font-semibold text-xl text-ivory m-0">
              Nutrient profile studies suggest
            </h2>
            <p className="mt-3 text-sm text-sage leading-relaxed m-0">
              Analyses of edible seaweeds report that red algae can contribute minerals, amino acids, and
              polysaccharides that behave like dietary fibre. A 2024 comparative study found{" "}
              <em className="text-ivory/90">C. crispus</em> comparatively rich in minerals such as calcium, iron,
              manganese, and zinc among the species tested (
              <ExtLink href="https://pmc.ncbi.nlm.nih.gov/articles/PMC11595611/">PMC11595611</ExtLink>
              ). Composition varies with harvest site, season, and processing — so labels and lab sheets beat viral
              “92 minerals” claims.
            </p>
            <p className="mt-3 text-sm text-sage leading-relaxed m-0">
              A broader{" "}
              <ExtLink href="https://pmc.ncbi.nlm.nih.gov/articles/PMC10817618/">2024 review of Chondrus</ExtLink>{" "}
              summarises many lab and animal findings (antioxidant assays, immunomodulation models, and more) while
              noting that clinical human evidence remains limited. That gap is why we phrase benefits as “studies
              suggest / traditionally used,” not guaranteed outcomes.
            </p>
          </DarkCard>

          <DarkCard as="section" aria-labelledby="food-tradition">
            <h2 id="food-tradition" className="font-sans font-semibold text-xl text-ivory m-0">
              Traditional food use, not a medical protocol
            </h2>
            <p className="mt-3 text-sm text-sage leading-relaxed m-0">
              Coastal kitchens have long boiled Irish moss into gels and broths. FAO materials document Irish moss as
              food and as a carrageenan raw material (
              <ExtLink href="https://www.fao.org/4/y4765e/y4765e04.htm">commercial seaweeds overview</ExtLink>
              ). That history supports curiosity about sea moss as a food ingredient — it does not prove modern
              wellness marketing claims. For how people actually use fridge-ready gel at home, see our{" "}
              <Link to="/learn/how-to-use-sea-moss-gel" className="underline hover:text-goldhover">
                how-to-use guide
              </Link>
              .
            </p>
          </DarkCard>

          <DarkCard as="section" aria-labelledby="iodine">
            <h2 id="iodine" className="font-sans font-semibold text-xl text-ivory m-0">
              Iodine: useful nutrient, portion-dependent
            </h2>
            <p className="mt-3 text-sm text-sage leading-relaxed m-0">
              Seaweed is a known dietary iodine source. The{" "}
              <ExtLink href="https://ods.od.nih.gov/factsheets/Iodine-Consumer/">NIH Office of Dietary Supplements</ExtLink>{" "}
              explains why iodine matters for thyroid hormone production — and that very high intakes can cause
              problems. A{" "}
              <ExtLink href="https://pubmed.ncbi.nlm.nih.gov/32803579/">2020 risk assessment</ExtLink> of retail red
              seaweeds (including Irish moss) discussed how moderate dried portions may fit dietary reference intakes,
              while extreme intakes could exceed upper limits. UK market research has also flagged inconsistent iodine
              labelling on seaweed products (
              <ExtLink href="https://pmc.ncbi.nlm.nih.gov/articles/PMC5302319/">PMC5302319</ExtLink>
              ). People who are pregnant, breastfeeding, managing thyroid conditions, or taking related medicines
              should ask a clinician before adding seaweed foods regularly.
            </p>
          </DarkCard>

          <DarkCard as="section" aria-labelledby="carrageenan">
            <h2 id="carrageenan" className="font-sans font-semibold text-xl text-ivory m-0">
              Carrageenan debates — a balanced note
            </h2>
            <p className="mt-3 text-sm text-sage leading-relaxed m-0">
              Carrageenan is the gel-forming polysaccharide extracted from certain red seaweeds and used widely as a
              food thickener. Regulatory reviews of{" "}
              <strong className="text-ivory font-medium">food-grade</strong> carrageenan (for example the{" "}
              <ExtLink href="https://efsa.onlinelibrary.wiley.com/doi/10.2903/j.efsa.2018.5238">
                2018 EFSA re-evaluation of E 407 / E 407a
              </ExtLink>
              ) have allowed continued use while calling for more data and treating the ADI as temporary. Separate
              laboratory work often uses degraded “poligeenan,” which is not authorised as a food additive — a
              distinction emphasised in{" "}
              <ExtLink href="https://pubmed.ncbi.nlm.nih.gov/24467586/">critical safety reviews</ExtLink>. Whole-food
              sea moss gel is not identical to purified industrial carrageenan; both topics deserve careful reading
              rather than social-media absolutes.
            </p>
          </DarkCard>

          <DarkCard as="section" aria-labelledby="limits">
            <h2 id="limits" className="font-sans font-semibold text-xl text-ivory m-0">
              What the evidence does not settle yet
            </h2>
            <p className="mt-3 text-sm text-sage leading-relaxed m-0">
              Public summaries (including{" "}
              <ExtLink href="https://www.nationalgeographic.com/health/article/sea-moss-health-benefits-effects">
                National Geographic’s 2025 overview
              </ExtLink>{" "}
              and clinician explainers such as{" "}
              <ExtLink href="https://health.clevelandclinic.org/sea-moss-benefits">Cleveland Clinic</ExtLink>) often
              note that seaweed research is broader than sea-moss-specific human trials. Early antioxidant or fibre
              findings are interesting; they are not proof of immunity “boosts,” detox, weight loss, or disease
              treatment. Seaweeds can also accumulate environmental metals depending on waters — another reason to
              favour transparent sourcing and food-style portions over megadoses.
            </p>
          </DarkCard>
        </div>

        <div className="rounded-[1.5rem] overflow-hidden border border-white/10 mb-12 max-w-4xl">
          <img
            src={kitchen.src}
            alt={kitchen.alt}
            width={kitchen.width}
            height={kitchen.height}
            className="w-full aspect-[21/9] object-cover"
          />
          <p className="px-4 py-2 text-[10px] text-sage/70 bg-panel/80">
            Fridge-ready food context — education about eating habits, not clinical outcomes.
          </p>
        </div>

        <section className="mb-14 max-w-4xl" aria-labelledby="videos">
          <h2 id="videos" className="font-sans font-semibold text-2xl text-ivory m-0 mb-2">
            Watch: educational videos
          </h2>
          <p className="text-sm text-sage leading-relaxed mb-6 max-w-3xl">
            Prefer university, public-broadcaster, or careful preparation videos over miracle-cure channels. Opens in
            YouTube’s player; we do not endorse every claim made in comments or related videos.
          </p>
          <div className="grid md:grid-cols-1 gap-8">
            {VIDEOS.map((v) => (
              <DarkCard key={v.id} className="overflow-hidden p-0 md:p-0">
                <div className="p-6 md:p-8 pb-4">
                  <h3 className="font-sans font-semibold text-lg text-ivory m-0">{v.title}</h3>
                  <p className="mt-2 text-sm text-sage leading-relaxed m-0">{v.blurb}</p>
                </div>
                <div className="relative w-full aspect-video bg-black/40">
                  <iframe
                    title={v.title}
                    src={`https://www.youtube-nocookie.com/embed/${v.id}`}
                    className="absolute inset-0 w-full h-full border-0"
                    loading="lazy"
                    allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                    allowFullScreen
                    referrerPolicy="strict-origin-when-cross-origin"
                  />
                </div>
                <p className="px-6 md:px-8 py-3 text-xs text-sage/80 m-0">
                  <ExtLink href={`https://www.youtube.com/watch?v=${v.id}`}>Open on YouTube</ExtLink>
                </p>
              </DarkCard>
            ))}
          </div>
        </section>

        <section className="mb-14 max-w-3xl" aria-labelledby="reading">
          <h2 id="reading" className="font-sans font-semibold text-2xl text-ivory m-0 mb-2">
            Research & reading
          </h2>
          <p className="text-sm text-sage leading-relaxed mb-6">
            Primary and secondary sources used for this page. Follow the links for full text or abstracts.
          </p>
          <ul className="space-y-4 list-none p-0 m-0">
            {READINGS.map((r) => (
              <li key={r.href}>
                <DarkCard className="!p-5">
                  <a
                    href={r.href}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="font-sans font-semibold text-base text-ivory hover:text-goldhover underline-offset-2 hover:underline m-0"
                  >
                    {r.title}
                  </a>
                  <p className="mt-2 text-sm text-sage leading-relaxed m-0">{r.note}</p>
                </DarkCard>
              </li>
            ))}
          </ul>
        </section>

        <WellnessDisclaimer className="max-w-3xl mb-12" />

        <div className="flex flex-wrap gap-3 mb-12">
          <Link to="/shop" className={`${HOME_CTA_DARK_PRIMARY} px-7 py-3.5 text-sm`}>
            Shop Moss Matrix
          </Link>
          <Link to="/learn/sea-moss-benefits" className={`${HOME_CTA_DARK_OUTLINE} px-7 py-3.5 text-sm`}>
            Benefits (education)
          </Link>
          <Link to="/learn/sea-moss-nutrition" className={`${HOME_CTA_DARK_OUTLINE} px-7 py-3.5 text-sm`}>
            Minerals & amino acids
          </Link>
          <Link to="/learn/what-is-sea-moss" className={`${HOME_CTA_DARK_OUTLINE} px-7 py-3.5 text-sm`}>
            What is sea moss?
          </Link>
        </div>

        <ConversionCtas />

        <p className="text-sm text-sage text-center mt-10">
          <Link to="/learn" className="underline hover:text-goldhover">
            Learn hub
          </Link>
          {" · "}
          <Link to="/learn/how-to-use-sea-moss-gel" className="underline hover:text-goldhover">
            How to use gel
          </Link>
          {" · "}
          <Link to="/faq" className="underline hover:text-goldhover">
            FAQ
          </Link>
        </p>
      </ContentPageLayout>
    </SiteShell>
  );
}
